import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export const THEMES = [
  {
    id: 'light',
    name: 'Classic Light',
    nameRw: 'Urumuri Rusanzwe',
    description: 'Clean & crisp modern newsroom style',
    isDark: false,
    previewBg: '#ffffff',
    previewBorder: '#e2e8f0',
    previewAccent: '#10b981',
  },
  {
    id: 'classic-blue',
    name: 'Classic Blue',
    nameRw: 'Ubururu bwa Classic',
    description: 'Clean & crisp modern style with vibrant blue accents',
    isDark: false,
    previewBg: '#ffffff',
    previewBorder: '#bfdbfe',
    previewAccent: '#007AFF',
    glowColor: 'rgba(0, 122, 255, 0.4)',
  },
  {
    id: 'dark',
    name: 'Onyx Dark',
    nameRw: 'Umwijima wa Onyx',
    description: 'Deep contrast slate dark mode',
    isDark: true,
    previewBg: '#0f172a',
    previewBorder: '#334155',
    previewAccent: '#10b981',
  },
  {
    id: 'cyber-glow',
    name: 'Cyber Emerald Glow',
    nameRw: 'Icyatsi Cyaka (Glow)',
    description: 'Obsidian with radiant emerald backlighting',
    isDark: true,
    previewBg: '#030806',
    previewBorder: '#10b981',
    previewAccent: '#34d399',
    glowColor: 'rgba(16, 185, 129, 0.6)',
  },
  {
    id: 'neon-violet',
    name: 'Neon Velvet Backlight',
    nameRw: 'Iriro rya Violet Cyaka',
    description: 'Royal midnight with electric amethyst aura',
    isDark: true,
    previewBg: '#080414',
    previewBorder: '#a855f7',
    previewAccent: '#c084fc',
    glowColor: 'rgba(168, 85, 247, 0.6)',
  },
  {
    id: 'sunset-amber',
    name: 'Sunset Amber Glow',
    nameRw: 'Zahabu Yaka (Sunset)',
    description: 'Twilight warmth with golden amber lighting',
    isDark: true,
    previewBg: '#0c0702',
    previewBorder: '#f59e0b',
    previewAccent: '#fbbf24',
    glowColor: 'rgba(245, 158, 11, 0.6)',
  },
];

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    let saved = localStorage.getItem('yegoNewsTheme');
    if (saved === 'imessage-blue') {
      saved = 'classic-blue';
      localStorage.setItem('yegoNewsTheme', 'classic-blue');
    }
    if (saved === 'light-glow') {
      saved = 'light';
      localStorage.setItem('yegoNewsTheme', 'light');
    }
    if (saved) return saved;
    // Check if user had legacy darkMode boolean
    const legacyDark = localStorage.getItem('yegoNewsDarkMode') === 'true';
    return legacyDark ? 'dark' : 'light';
  });

  const [fontSize, setFontSizeState] = useState(() => {
    const saved = localStorage.getItem('yegoNewsFontSize');
    if (saved) return saved;
    const settings = localStorage.getItem('yegoNewsSettings');
    if (settings) {
      try {
        const parsed = JSON.parse(settings);
        if (parsed.fontSize) return parsed.fontSize;
      } catch (e) {}
    }
    return 'medium';
  });

  // Apply Theme & Font Size to Document Root
  useEffect(() => {
    const root = document.documentElement;

    // 1. Apply Font Size
    root.classList.remove('font-size-small', 'font-size-medium', 'font-size-large');
    root.classList.add(`font-size-${fontSize}`);
    if (fontSize === 'small') {
      root.style.fontSize = '14px';
    } else if (fontSize === 'large') {
      root.style.fontSize = '18.5px';
    } else {
      root.style.fontSize = '16px';
    }

    // 2. Apply Theme
    const allThemeClasses = THEMES.map(t => `theme-${t.id}`);
    root.classList.remove('dark', ...allThemeClasses);

    const activeThemeObj = THEMES.find(t => t.id === theme) || THEMES[0];
    root.classList.add(`theme-${activeThemeObj.id}`);

    if (activeThemeObj.isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Persist
    localStorage.setItem('yegoNewsTheme', theme);
    localStorage.setItem('yegoNewsFontSize', fontSize);
    localStorage.setItem('yegoNewsDarkMode', activeThemeObj.isDark ? 'true' : 'false');
  }, [theme, fontSize]);

  const setTheme = (newTheme) => {
    setThemeState(newTheme);
  };

  const setFontSize = (newSize) => {
    setFontSizeState(newSize);
  };

  const activeThemeObj = THEMES.find(t => t.id === theme) || THEMES[0];
  const darkMode = Boolean(activeThemeObj?.isDark);

  const setDarkMode = (isDark) => {
    if (isDark) {
      setThemeState(activeThemeObj?.isDark ? theme : 'dark');
    } else {
      setThemeState('light');
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        fontSize,
        setFontSize,
        darkMode,
        setDarkMode,
        availableThemes: THEMES,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
